import React, { useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CSVImportModal = ({ isOpen, onClose, type, onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setLoading(true);
    setResults(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      let endpoint;
      if (type === 'companies') {
        endpoint = '/import/companies';
      } else if (type === 'contacts') {
        endpoint = '/import/contacts';
      } else if (type === 'transporters') {
        endpoint = '/import/transporters';
      } else {
        throw new Error(`Type d'import non supporté: ${type}`);
      }
      
      const response = await axios.post(`${API}${endpoint}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
      });

      setResults(response.data);
      
      // Notifier le parent du succès
      if (onImportComplete) {
        onImportComplete();
      }
    } catch (error) {
      console.error('Erreur lors de l\'import:', error);
      
      // Gestion spécifique des erreurs d'authentification
      if (error.response?.status === 401 || error.response?.status === 403) {
        setResults({
          success: false,
          message: '❌ Erreur d\'authentification. Veuillez vous reconnecter.',
          errors: [{ 
            line: 0, 
            message: 'Session expirée ou non autorisé', 
            field: 'authentication' 
          }]
        });
      } else if (error.response?.status >= 400 && error.response?.status < 500) {
        // Erreurs client (format de fichier, validation, etc.)
        const errorData = error.response.data || {};
        setResults({
          success: false,
          message: errorData.detail || `Erreur lors de l'import: ${error.response.statusText}`,
          errors: errorData.errors || [{ 
            line: 0, 
            message: errorData.detail || 'Erreur de validation du fichier', 
            field: 'file' 
          }]
        });
      } else if (error.response?.status >= 500) {
        // Erreurs serveur
        setResults({
          success: false,
          message: '❌ Erreur serveur. Veuillez réessayer plus tard.',
          errors: [{ 
            line: 0, 
            message: 'Erreur interne du serveur', 
            field: 'server' 
          }]
        });
      } else {
        // Erreurs réseau ou autres
        setResults({
          success: false,
          message: 'Erreur lors de l\'import: ' + (error.response?.data?.message || error.message),
          errors: error.response?.data?.errors || [{ 
            line: 0, 
            message: error.message || 'Erreur inconnue', 
            field: 'network' 
          }]
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setFile(null);
    setResults(null);
    setLoading(false);
    setDragActive(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  const csvTemplate = type === 'companies' 
    ? `nom,email,telephone,adresse,ville,pays,type_contact,rccm,industrie,compte_contribuable,responsable_compte,statut,notes
Société Exemple,contact@exemple.com,+225 01 02 03 04,"Cocody Abidjan",Abidjan,Côte d'Ivoire,Client actuel,CI-ABJ-2023-B-12345,Logistique,1234567890,Jean Dupont,active,Notes exemple`
    : type === 'transporters'
    ? `nom,contact_person,email,telephone,phone_secondary,cni,zone_couverture,type_transporteur,specialites,types_vehicules,permis_conduire_valide,carte_transporteur_valide,notes,active
Transporteur Exemple,Jean Kouassi,jean@transport.ci,+225 01 02 03 04,+225 05 06 07 08,CI123456789,Abidjan;Bouaké;San-Pédro,independant,routier;maritime,Camion 3.5T;Semi-remorque,true,true,Transporteur fiable,true`
    : `nom,prenom,fonction,email,telephone,telephone_mobile,role,societe,notes,is_active
Dupont,Jean,Directeur Commercial,jean@exemple.com,+225 01 02 03 04,+225 05 06 07 08,Commercial,Société Exemple,Contact principal,true`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Import CSV - {type === 'companies' ? 'Sociétés' : type === 'transporters' ? 'Transporteurs' : 'Contacts'}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ×
          </button>
        </div>

        {!results ? (
          <>
            {/* Format Template */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">
                📋 Format CSV attendu:
              </h4>
              <div className="text-xs font-mono bg-white p-2 rounded border overflow-x-auto">
                <pre>{csvTemplate}</pre>
              </div>
              <div className="mt-2 text-sm text-blue-700">
                <strong>Instructions:</strong>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Première ligne = en-têtes (obligatoire)</li>
                  <li>Séparateur: virgule (,)</li>
                  <li>Encodage: UTF-8</li>
                  {type === 'companies' && (
                    <>
                      <li>ville: nom de la ville (ex: Abidjan, Bouaké)</li>
                      <li>pays: pays par défaut "Côte d'Ivoire"</li>
                      <li>type_contact: "Client potentiel", "Client actuel" ou "Autre"</li>
                    </>
                  )}
                  {type === 'transporters' && (
                    <>
                      <li>zone_couverture: zones séparées par ";" (ex: Abidjan;Bouaké)</li>
                      <li>type_transporteur: "independant", "representant" ou "societe"</li>
                      <li>specialites: séparées par ";" (ex: routier;maritime)</li>
                      <li>types_vehicules: séparés par ";" (ex: Camion 3.5T;Semi-remorque)</li>
                      <li>permis_conduire_valide, carte_transporteur_valide: true/false</li>
                    </>
                  )}
                  {type === 'contacts' && <li>societe: nom exact de la société existante</li>}
                  <li>Doublons détectés automatiquement</li>
                </ul>
              </div>
            </div>

            {/* File Upload */}
            <div className="mb-6">
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  dragActive
                    ? 'border-green-400 bg-green-50'
                    : 'border-gray-300 hover:border-green-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="text-4xl mb-4">📄</div>
                <div className="text-gray-600">
                  <p className="text-lg font-medium mb-2">
                    Glissez votre fichier CSV ici
                  </p>
                  <p className="text-sm">ou</p>
                </div>
                <label className="mt-4 inline-block bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 cursor-pointer transition-colors">
                  Parcourir les fichiers
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleChange}
                    className="hidden"
                  />
                </label>
              </div>

              {file && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-green-500 mr-2">📄</span>
                      <span className="text-sm font-medium">{file.name}</span>
                      <span className="text-xs text-gray-500 ml-2">
                        ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    <button
                      onClick={() => setFile(null)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Confirmation Section */}
            {file && !results && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">
                  🔍 Prêt pour l'import
                </h4>
                <div className="text-sm text-blue-700 space-y-1">
                  <p><strong>Fichier sélectionné :</strong> {file.name}</p>
                  <p><strong>Taille :</strong> {(file.size / 1024).toFixed(1)} KB</p>
                  <p><strong>Type d'import :</strong> {type === 'companies' ? 'Sociétés' : 'Contacts'}</p>
                  <p className="mt-2 font-medium">
                    L'import va traiter votre fichier et ajouter/mettre à jour les données dans la base.
                  </p>
                </div>
              </div>
            )}

            {/* Import Button */}
            <div className="flex gap-3">
              <button
                onClick={handleImport}
                disabled={!file || loading}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                  !file || loading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Import en cours...
                  </div>
                ) : (
                  '📥 Lancer l\'import'
                )}
              </button>
              <button
                onClick={handleClose}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
            </div>
          </>
        ) : (
          /* Results */
          <div className="space-y-4">
            {/* Main Result Status */}
            <div className={`p-4 rounded-lg border ${
              results.success 
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}>
              <div className="flex items-center mb-2">
                <span className="text-2xl mr-2">
                  {results.success ? '✅' : '❌'}
                </span>
                <h4 className="font-semibold">
                  {results.success ? 'Import réussi!' : 'Échec de l\'import'}
                </h4>
              </div>
              <p>{results.message}</p>
              
              {/* Additional result details */}
              <div className="mt-3 text-sm">
                <p><strong>Fichier traité :</strong> {file?.name || 'Fichier CSV'}</p>
                {results.total_processed && (
                  <p><strong>Lignes traitées :</strong> {results.total_processed}</p>
                )}
              </div>
            </div>

            {/* Success Statistics */}
            {(results.created > 0 || results.updated > 0) && (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="text-2xl font-bold text-blue-600">
                    {results.created || 0}
                  </div>
                  <div className="text-sm text-blue-700">📊 Nouvelles entrées créées</div>
                  {results.created > 0 && (
                    <div className="text-xs text-blue-600 mt-1">
                      Ajoutées à la base de données
                    </div>
                  )}
                </div>
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <div className="text-2xl font-bold text-orange-600">
                    {results.updated || 0}
                  </div>
                  <div className="text-sm text-orange-700">🔄 Entrées mises à jour</div>
                  {results.updated > 0 && (
                    <div className="text-xs text-orange-600 mt-1">
                      Doublons détectés et mis à jour
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Import Summary */}
            {results.success && (
              <div className="bg-gray-50 p-4 rounded-lg border">
                <h5 className="font-medium text-gray-800 mb-2">
                  📋 Résumé de l'import
                </h5>
                <div className="text-sm text-gray-700 space-y-1">
                  <p>
                    <strong>Total traité :</strong> {(results.created || 0) + (results.updated || 0)} {type === 'companies' ? 'sociétés' : 'contacts'}
                  </p>
                  {results.created > 0 && (
                    <p>• {results.created} nouvelle(s) entrée(s) ajoutée(s)</p>
                  )}
                  {results.updated > 0 && (
                    <p>• {results.updated} entrée(s) mise(s) à jour</p>
                  )}
                  {(!results.errors || results.errors.length === 0) && (
                    <p className="text-green-600 font-medium">✅ Aucune erreur détectée</p>
                  )}
                </div>
              </div>
            )}

            {/* Detailed Errors */}
            {results.errors && results.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h5 className="font-medium text-red-800 mb-2">
                  ❌ Erreurs détectées ({results.errors.length}):
                </h5>
                <div className="max-h-40 overflow-y-auto">
                  {results.errors.map((error, index) => (
                    <div key={index} className="text-sm text-red-700 mb-2 p-2 bg-red-100 rounded">
                      <div className="font-medium">Ligne {error.line}:</div>
                      <div>{error.message}</div>
                      {error.field && (
                        <div className="text-xs text-red-600">Champ concerné: {error.field}</div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-xs text-red-600">
                  💡 Tip: Corrigez ces erreurs dans votre fichier CSV et relancez l'import
                </div>
              </div>
            )}

            {/* Warnings */}
            {results.warnings && results.warnings.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h5 className="font-medium text-yellow-800 mb-2">
                  Avertissements ({results.warnings.length}):
                </h5>
                <div className="max-h-40 overflow-y-auto">
                  {results.warnings.map((warning, index) => (
                    <div key={index} className="text-sm text-yellow-700 mb-1 p-2 bg-yellow-100 rounded">
                      {warning}
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-xs text-yellow-600">
                  💡 Ces avertissements n'empêchent pas l'import mais méritent votre attention
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  resetModal();
                }}
                className="flex-1 bg-green-500 text-white py-3 px-4 rounded-lg hover:bg-green-600 transition-colors font-medium"
              >
                📤 Faire un nouvel import
              </button>
              <button
                onClick={handleClose}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Fermer
              </button>
            </div>
            
            {/* Additional Actions for Successful Imports */}
            {results.success && ((results.created || 0) > 0 || (results.updated || 0) > 0) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h5 className="font-medium text-blue-800 mb-2">
                  🎯 Prochaines étapes suggérées :
                </h5>
                <div className="text-sm text-blue-700 space-y-1">
                  <p>• Consultez la liste des {type === 'companies' ? 'sociétés' : 'contacts'} pour vérifier les données importées</p>
                  <p>• Vérifiez que tous les champs ont été correctement renseignés</p>
                  {results.updated > 0 && (
                    <p>• Contrôlez les entrées mises à jour pour vous assurer des modifications</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CSVImportModal;